import { useState, useEffect, useRef } from 'react';
import { useGetChecklistNotes, useCreateChecklistNote } from '@/pages/company-profile/checklist/useChecklistNotes';
import { IChecklistNote } from '@/types/checklist.types';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Loader2, Send } from 'lucide-react';
import { SubmitButton } from '@/components/ui/Button';

interface ChecklistNotesProps {
  tenantId: string | number;
  instanceId: number;
}

export const ChecklistNotes = ({ tenantId, instanceId }: ChecklistNotesProps) => {
  const [noteText, setNoteText] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetChecklistNotes({
    tenantId,
    instanceId,
    pageSize: 20,
  });

  const createNoteMutation = useCreateChecklistNote();

  // Flatten all notes from all pages
  const allNotes: IChecklistNote[] = data?.pages.flatMap(page => page.notes) || [];

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    createNoteMutation.mutate(
      {
        tenantId,
        instanceId,
        payload: { note: noteText },
      },
      {
        onSuccess: () => {
          setNoteText('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Input Section */}
      <form
        onSubmit={handleSubmit}
        className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700"
      >
        <label
          htmlFor="note-input"
          className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider"
        >
          Tambah Catatan Baru
        </label>
        <textarea
          id="note-input"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
          placeholder="Tulis catatan Anda di sini..."
          rows={3}
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
        />
        <div className="mt-2 flex justify-end">
          <SubmitButton
            type="submit"
            disabled={!noteText.trim() || createNoteMutation.isLoading}
            className="flex items-center gap-2 py-1.5 px-4 text-xs h-auto"
          >
            {createNoteMutation.isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Send size={14} />
                Simpan
              </>
            )}
          </SubmitButton>
        </div>
      </form>

      {/* Notes List Section */}
      <div className="mt-2">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        )}

        {!isLoading && allNotes.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-xl">
            <p className="text-gray-400 dark:text-gray-500 text-sm">Belum ada catatan.</p>
          </div>
        )}

        {!isLoading && allNotes.length > 0 && (
          <div ref={scrollContainerRef} className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {allNotes.map(note => (
              <div
                key={note.id}
                className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm"
              >
                <div className="flex justify-between items-start mb-1.5 gap-2">
                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                    {note.created_by_name}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap pt-0.5">
                    {format(new Date(note.created_at), 'dd/MM/yy HH:mm', { locale: idLocale })}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                  {note.note}
                </p>
              </div>
            ))}

            {/* Infinite scroll trigger */}
            <div ref={observerTarget} className="h-4">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
